import { useParams, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { contractsApi, auditLogsApi, renewalsApi, type Contract, type AuditLog, type ContractRenewal, type HealthScore, ApiError } from "@/lib/api";
import { statusColors, statusLabels, type ContractStatus } from "@/lib/constants";
import { useAuth } from "@/lib/AuthContext";
import AuditTimeline from "./components/AuditTimeline";
import HealthScoreCard from "./components/HealthScoreCard";
import RenewalWorkflowTab from "./components/RenewalWorkflowTab";
import ComplianceCard from "@/pages/supplier-performance/components/ComplianceCard";

function formatCurrency(value: string | null): string {
  if (!value) return "—";
  const n = parseFloat(value);
  return `R ${(n / 1_000_000).toFixed(1)}M`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_OPTIONS: ContractStatus[] = ["active", "pending_renewal", "expired", "terminated"];

const RENEWAL_DECISIONS = [
  { value: "auto_renew" as const, label: "Auto-Renew", desc: "Supplier meets expectations — approve renewal automatically", color: "text-secondary-600" },
  { value: "conditional" as const, label: "Conditional Renewal", desc: "Renew with specific improvement conditions attached", color: "text-accent-600" },
  { value: "re_advertise" as const, label: "Re-Advertise", desc: "Performance unacceptable — initiate new procurement", color: "text-foreground-600" },
  { value: "extend" as const, label: "Extend", desc: "Extend the contract term with a new end date", color: "text-primary-600" },
  { value: "terminate" as const, label: "Terminate", desc: "End the contract immediately", color: "text-foreground-700" },
];

export default function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<"overview" | "workflow">("overview");
  const [contract, setContract] = useState<Contract | null>(null);
  const [healthScore, setHealthScore] = useState<HealthScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingDocument, setSavingDocument] = useState(false);

  const [status, setStatus] = useState<ContractStatus>("active");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<ContractStatus | null>(null);
  const [statusNotes, setStatusNotes] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);
  const [acknowledgeNotes, setAcknowledgeNotes] = useState("");

  // Renewal workflow state
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalDecision, setRenewalDecision] = useState<ContractRenewal["decision"]>("auto_renew");
  const [renewalJustification, setRenewalJustification] = useState("");
  const [renewalNewEndDate, setRenewalNewEndDate] = useState("");
  const [savingRenewal, setSavingRenewal] = useState(false);
  const [renewals, setRenewals] = useState<ContractRenewal[]>([]);

  // Persistent audit log
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  useEffect(() => {
    if (!id) return;
    const contractId = Number(id);
    setLoading(true);

    Promise.all([
      contractsApi.get(contractId),
      auditLogsApi.forContract(contractId).catch(() => ({ data: [] as AuditLog[] })),
      renewalsApi.forContract(contractId).catch(() => ({ data: [] as ContractRenewal[] })),
    ]).then(([contractRes, logsRes, renewalsRes]) => {
      setContract(contractRes.data);
      setHealthScore(contractRes.health_score);
      setStatus(contractRes.data.status as ContractStatus);
      setAuditLogs(logsRes.data);
      setRenewals(renewalsRes.data);
    }).catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load contract"))
      .finally(() => setLoading(false));
  }, [id]);

  const hasUnacknowledgedAlert = useMemo(() => {
    const alerts = auditLogs.filter((e) => e.action_type === "alert_sent").length;
    const acks = auditLogs.filter((e) => e.action_type === "alert_acknowledged").length;
    return alerts > acks;
  }, [auditLogs]);

  const addAuditLog = async (action_type: string, action: string, notes?: string) => {
    if (!contract) return;
    try {
      const res = await auditLogsApi.create(contract.id, { action_type, action, notes });
      setAuditLogs((prev) => [res.data, ...prev]);
    } catch {
      // non-fatal, just don't update local state
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-foreground-400 text-sm">
        <i className="ri-loader-4-line animate-spin mr-2"></i>Loading...
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <i className="ri-file-unknow-line text-foreground-300 text-5xl mb-4"></i>
        <p className="text-foreground-500 text-sm">{error ?? "Contract not found."}</p>
        <button onClick={() => navigate("/contracts")}
          className="mt-4 h-9 px-4 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
          Back to Contracts
        </button>
      </div>
    );
  }

  const st = statusColors[status] ?? statusColors.active;

  const openStatusModal = (newStatus: ContractStatus) => {
    setPendingStatus(newStatus);
    setStatusNotes("");
    setShowStatusModal(true);
    setShowStatusDropdown(false);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus) return;
    setSavingStatus(true);
    try {
      const res = await contractsApi.update(contract.id, { status: pendingStatus, status_notes: statusNotes } as Partial<Contract>);
      setStatus(pendingStatus);
      setHealthScore(res.health_score);
      await addAuditLog(
        "status_change",
        `Status changed from ${statusLabels[status]} to ${statusLabels[pendingStatus]}`,
        statusNotes.trim() || undefined,
      );
    } catch {
      // keep current status
    } finally {
      setSavingStatus(false);
      setShowStatusModal(false);
      setPendingStatus(null);
      setStatusNotes("");
    }
  };

  const toggleSignedDocument = async () => {
    if (!contract) return;
    setSavingDocument(true);
    try {
      const res = await contractsApi.update(contract.id, { has_signed_document: !contract.has_signed_document });
      setContract(res.data);
      setHealthScore(res.health_score);
    } catch {
      // keep current value
    } finally {
      setSavingDocument(false);
    }
  };

  const confirmAcknowledge = async () => {
    await addAuditLog(
      "alert_acknowledged",
      "Alert Acknowledged",
      acknowledgeNotes.trim() || "All outstanding alerts have been reviewed.",
    );
    setShowAcknowledgeModal(false);
    setAcknowledgeNotes("");
  };

  const confirmRenewal = async () => {
    setSavingRenewal(true);
    try {
      const res = await renewalsApi.create(contract.id, {
        decision: renewalDecision,
        justification: renewalJustification,
        new_end_date: renewalNewEndDate || undefined,
      });
      setRenewals((prev) => [res.data, ...prev]);
      // refresh audit logs to pick up the auto-logged renewal entry
      const logsRes = await auditLogsApi.forContract(contract.id);
      setAuditLogs(logsRes.data);
      setShowRenewalModal(false);
      setRenewalJustification("");
      setRenewalNewEndDate("");
    } catch {
      // keep modal open
    } finally {
      setSavingRenewal(false);
    }
  };

  // Map audit logs to the AuditTimeline's expected shape
  const timelineEntries = auditLogs.map((log) => ({
    id: String(log.id),
    contractId: log.contract_id,
    action: log.action,
    actionType: log.action_type as "created" | "alert_sent" | "alert_acknowledged" | "status_change" | "document_uploaded" | "note_added" | "escalation" | "workflow_started" | "workflow_step_completed" | "workflow_step_skipped" | "workflow_step_reopened" | "workflow_step_overdue" | "workflow_completed" | "renewal_decision",
    performedBy: log.user?.name ?? "System",
    notes: log.notes ?? "",
    createdAt: log.created_at,
  }));

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/contracts")}
            className="w-8 h-8 rounded-md flex items-center justify-center border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-sm"></i>
          </button>
          <div>
            <h1 className="text-primary-600 text-xl md:text-2xl font-bold">{contract.vendor_name}</h1>
            <p className="text-foreground-500 text-sm">{contract.contract_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate(`/contracts/${contract.id}/edit`)}
            className="h-9 px-4 border border-background-300 text-foreground-600 text-sm font-medium rounded-md hover:bg-background-100 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
            <i className="ri-edit-line text-sm"></i><span>Edit</span>
          </button>

          {status === "pending_renewal" && (
            <button onClick={() => setShowRenewalModal(true)}
              className="h-9 px-4 bg-secondary-500 text-white text-sm font-medium rounded-md hover:bg-secondary-600 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
              <i className="ri-refresh-line text-sm"></i><span>Record Renewal Decision</span>
            </button>
          )}

          {hasUnacknowledgedAlert && (
            <button onClick={() => { setAcknowledgeNotes(""); setShowAcknowledgeModal(true); }}
              className="h-9 px-4 bg-accent-500 text-white text-sm font-medium rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
              <i className="ri-check-double-line text-sm"></i><span>Acknowledge Alert</span>
            </button>
          )}

          <div className="relative">
            <button onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className="h-9 px-4 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
              <span>Change Status</span>
              <i className={`${showStatusDropdown ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`}></i>
            </button>
            {showStatusDropdown && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setShowStatusDropdown(false)}></div>
                <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg border border-background-200 shadow-lg z-30 py-1">
                  {STATUS_OPTIONS.map((opt) => (
                    <button key={opt} onClick={() => openStatusModal(opt)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2.5 ${status === opt ? "font-semibold text-primary-600 bg-background-50" : "text-foreground-700"}`}>
                      <span className={`w-2 h-2 rounded-full shrink-0 ${statusColors[opt].dot}`}></span>
                      {statusLabels[opt]}
                      {status === opt && <i className="ri-check-line text-primary-500 ml-auto text-sm"></i>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-background-200 mb-6">
        <button
          onClick={() => setActiveTab("overview")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === "overview" ? "border-primary-600 text-primary-600" : "border-transparent text-foreground-500 hover:text-foreground-700"
          }`}>
          Overview
        </button>
        <button
          onClick={() => setActiveTab("workflow")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === "workflow" ? "border-primary-600 text-primary-600" : "border-transparent text-foreground-500 hover:text-foreground-700"
          }`}>
          Renewal Workflow
        </button>
      </div>

      {activeTab === "workflow" && <RenewalWorkflowTab contractId={contract.id} />}

      {activeTab === "overview" && (
      <>
      {/* Contract Health Score */}
      {healthScore && <HealthScoreCard healthScore={healthScore} />}

      {/* Contract Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <DetailCard label="Supplier" value={contract.vendor_name} />
        <DetailCard label="Contract / Bid Number" value={contract.contract_number} />
        <DetailCard label="Category" value={contract.contract_type ?? "—"} />
        <DetailCard label="Contract Value" value={formatCurrency(contract.contract_value)} />
        <DetailCard label="Start Date" value={formatDate(contract.start_date)} />
        <DetailCard label="End Date" value={formatDate(contract.end_date)} />
        {contract.original_term_months && (
          <DetailCard label="Original Term" value={`${contract.original_term_months} months`} />
        )}
        <DetailCard label="Extension Allowed" value={contract.extension_allowed ? "Yes" : "No"}
          valueColor={contract.extension_allowed ? "text-primary-600" : "text-foreground-600"} />
        {contract.extension_allowed && contract.extension_conditions && (
          <DetailCard label="Extension Conditions" value={contract.extension_conditions} />
        )}
        {contract.responsible_officer && (
          <DetailCard label="Responsible Officer" value={contract.responsible_officer} />
        )}
        <DetailCard label="Department" value={contract.department} />
        <DetailCard label="Current Status" value={
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
            {statusLabels[status]}
          </span>
        } />
      </div>

      {contract.supplier && (
        <ComplianceCard
          supplier={contract.supplier}
          onUpdated={(updated) => setContract((prev) => prev ? { ...prev, supplier: updated } : prev)}
        />
      )}

      {/* Notes */}
      {contract.notes && (
        <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
          <h2 className="text-primary-600 text-base font-semibold mb-2">Notes</h2>
          <p className="text-foreground-600 text-sm leading-relaxed">{contract.notes}</p>
        </div>
      )}

      {/* Renewal History */}
      {renewals.length > 0 && (
        <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
          <h2 className="text-primary-600 text-base font-semibold mb-4">Renewal Decisions</h2>
          <div className="space-y-3">
            {renewals.map((r) => {
              const dec = RENEWAL_DECISIONS.find((d) => d.value === r.decision);
              return (
                <div key={r.id} className="flex items-start gap-3 p-3 bg-background-50 rounded-lg border border-background-200">
                  <div className="w-8 h-8 rounded-full bg-secondary-50 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-refresh-line text-secondary-500 text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-sm font-semibold ${dec?.color ?? "text-foreground-700"}`}>{dec?.label ?? r.decision}</span>
                      <span className="text-foreground-400 text-xs">·</span>
                      <span className="text-foreground-400 text-xs">{new Date(r.decided_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}</span>
                      {r.decided_by && <span className="text-foreground-400 text-xs">by {r.decided_by.name}</span>}
                    </div>
                    <p className="text-foreground-600 text-sm">{r.justification}</p>
                    {r.new_end_date && (
                      <p className="text-foreground-400 text-xs mt-1">New end date: {formatDate(r.new_end_date)}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Documents */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-primary-600 text-base font-semibold">Documents</h2>
          <button className="h-8 px-3 border border-background-300 text-foreground-600 text-xs font-medium rounded-md hover:bg-background-100 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
            <i className="ri-upload-line text-sm"></i><span>Upload</span>
          </button>
        </div>
        <label className="flex items-center gap-2.5 px-1 py-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={contract.has_signed_document}
            disabled={savingDocument}
            onChange={toggleSignedDocument}
            className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-500/30 cursor-pointer"
          />
          <span className="text-foreground-700 text-sm">Signed contract document on file</span>
          {savingDocument && <i className="ri-loader-4-line animate-spin text-foreground-400 text-sm"></i>}
        </label>
        <div className="py-6 text-center text-foreground-400 text-sm">
          <i className="ri-folder-open-line text-3xl mb-2 block"></i>
          No documents attached yet.
        </div>
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <div className="flex items-center gap-3 mb-5">
          <h2 className="text-primary-600 text-base font-semibold">Audit Trail</h2>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[11px] font-medium">
            {loadingAudit ? "…" : `${auditLogs.length} entr${auditLogs.length !== 1 ? "ies" : "y"}`}
          </span>
        </div>
        {auditLogs.length === 0 ? (
          <p className="text-foreground-400 text-sm text-center py-6">No activity recorded yet.</p>
        ) : (
          <AuditTimeline entries={timelineEntries} />
        )}
      </div>
      </>
      )}

      {/* Status Change Modal */}
      {showStatusModal && pendingStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowStatusModal(false)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-accent-50 flex items-center justify-center shrink-0">
                <i className="ri-arrow-left-right-line text-accent-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-foreground-900 text-base font-semibold">Confirm Status Change</h3>
                <p className="text-foreground-400 text-xs">This action will be recorded in the audit trail</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-foreground-500 text-xs font-medium">Current</span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>{statusLabels[status]}
                </span>
              </div>
              <div className="flex items-center justify-center mb-1">
                <i className="ri-arrow-down-line text-foreground-300 text-lg"></i>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-500 text-xs font-medium">New</span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[pendingStatus].bg} ${statusColors[pendingStatus].text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusColors[pendingStatus].dot}`}></span>{statusLabels[pendingStatus]}
                </span>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-foreground-600 text-xs font-semibold mb-1.5">
                Notes <span className="text-foreground-400 font-normal">(required)</span>
              </label>
              <textarea value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Explain the reason for this status change..."
                maxLength={500} rows={3}
                className="w-full px-3 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-700 bg-white placeholder:text-foreground-300 resize-none focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-200" />
              <p className="text-foreground-400 text-[10px] mt-1 text-right">{statusNotes.length}/500</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowStatusModal(false)}
                className="flex-1 h-10 border border-background-300 text-foreground-600 text-sm font-medium rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                Cancel
              </button>
              <button onClick={confirmStatusChange} disabled={!statusNotes.trim() || savingStatus}
                className="flex-1 h-10 bg-accent-500 text-white text-sm font-semibold rounded-lg hover:bg-accent-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-2">
                {savingStatus && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Acknowledge Modal */}
      {showAcknowledgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAcknowledgeModal(false)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-secondary-50 flex items-center justify-center shrink-0">
                <i className="ri-check-double-line text-secondary-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-foreground-900 text-base font-semibold">Acknowledge Alert</h3>
                <p className="text-foreground-400 text-xs">Confirm you have reviewed all outstanding alerts</p>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-foreground-600 text-xs font-semibold mb-1.5">Notes <span className="text-foreground-400 font-normal">(optional)</span></label>
              <textarea value={acknowledgeNotes} onChange={(e) => setAcknowledgeNotes(e.target.value)}
                placeholder="Add any context about your review..."
                maxLength={500} rows={2}
                className="w-full px-3 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-700 bg-white placeholder:text-foreground-300 resize-none focus:outline-none focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200" />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowAcknowledgeModal(false)}
                className="flex-1 h-10 border border-background-300 text-foreground-600 text-sm font-medium rounded-lg hover:bg-background-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={confirmAcknowledge}
                className="flex-1 h-10 bg-secondary-500 text-white text-sm font-semibold rounded-lg hover:bg-secondary-600 transition-colors cursor-pointer">
                Confirm Acknowledgment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renewal Decision Modal */}
      {showRenewalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowRenewalModal(false)}></div>
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6 z-10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-secondary-50 flex items-center justify-center shrink-0">
                <i className="ri-refresh-line text-secondary-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-foreground-900 text-base font-semibold">Record Renewal Decision</h3>
                <p className="text-foreground-400 text-xs">{contract.contract_number} · {contract.vendor_name}</p>
              </div>
            </div>

            <div className="mb-5">
              <p className="text-foreground-500 text-xs font-semibold uppercase tracking-wider mb-2">Decision</p>
              <div className="space-y-2">
                {RENEWAL_DECISIONS.map((dec) => (
                  <button key={dec.value} onClick={() => setRenewalDecision(dec.value)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors cursor-pointer ${renewalDecision === dec.value ? "border-primary-400 bg-primary-50" : "border-background-200 hover:border-background-300"}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${renewalDecision === dec.value ? "border-primary-500" : "border-background-300"}`}>
                        {renewalDecision === dec.value && <div className="w-2 h-2 rounded-full bg-primary-500"></div>}
                      </div>
                      <span className={`text-sm font-semibold ${renewalDecision === dec.value ? "text-primary-600" : "text-foreground-700"}`}>{dec.label}</span>
                    </div>
                    <p className="text-foreground-400 text-xs ml-[22px]">{dec.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {renewalDecision === "extend" && (
              <div className="mb-4">
                <label className="block text-foreground-600 text-xs font-semibold mb-1.5">New End Date</label>
                <input type="date" value={renewalNewEndDate} onChange={(e) => setRenewalNewEndDate(e.target.value)}
                  className="w-full h-9 px-3 border border-background-300 rounded-lg text-sm text-foreground-700 focus:outline-none focus:border-primary-400" />
              </div>
            )}

            <div className="mb-5">
              <label className="block text-foreground-600 text-xs font-semibold mb-1.5">
                Justification <span className="text-accent-500">*</span>
              </label>
              <textarea value={renewalJustification} onChange={(e) => setRenewalJustification(e.target.value)}
                placeholder="Provide the rationale for this renewal decision. Reference supplier reviews, SLA performance, or procurement constraints..."
                maxLength={1000} rows={4}
                className="w-full px-3 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-700 bg-white placeholder:text-foreground-300 resize-none focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200" />
              <p className="text-foreground-400 text-[10px] mt-1 text-right">{renewalJustification.length}/1000</p>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => setShowRenewalModal(false)}
                className="flex-1 h-10 border border-background-300 text-foreground-600 text-sm font-medium rounded-lg hover:bg-background-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={confirmRenewal} disabled={!renewalJustification.trim() || savingRenewal || (renewalDecision === "extend" && !renewalNewEndDate)}
                className="flex-1 h-10 bg-secondary-500 text-white text-sm font-semibold rounded-lg hover:bg-secondary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-2">
                {savingRenewal && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                Record Decision
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailCard({ label, value, valueColor }: { label: string; value: string | React.ReactNode; valueColor?: string }) {
  return (
    <div className="bg-white rounded-lg border border-background-200 p-4">
      <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-1">{label}</p>
      {typeof value === "string" ? (
        <p className={`text-foreground-800 text-sm font-semibold ${valueColor ?? ""}`}>{value}</p>
      ) : (
        <div>{value}</div>
      )}
    </div>
  );
}
